/**
 * Réglages des bons d'intervention (« bon refondu » du 2026-10-16, ADR-0014 §13) : les natures de bon et, pour chacune, la
 * liste de ses CONTRÔLES — « Courroies / accouplement », « T° d'huile »… — telle que le papier les imprime, trois colonnes,
 * dans l'ordre choisi ici.
 *
 * Mise en page standard : la liste des natures à gauche, la nature choisie à droite (deux défilements). On y règle le libellé,
 * l'option « enregistre un entretien réalisé », les contrôles (ajouter, renommer, ordonner, retirer) ; une nature s'archive et se
 * réactive, jamais ne se supprime. L'enregistrement porte la révision lue : si quelqu'un d'autre a modifié la nature entre-temps,
 * l'écran le dit au lieu d'écraser son travail.
 *
 * Modifier la liste d'une nature ne réécrit JAMAIS un bon existant : un bon garde l'instantané des libellés qu'il a reçus. Les
 * bons à saisir ensuite reprennent la liste d'aujourd'hui.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, api, type ModeleBon, type NatureBon } from '../api/client';
import { useDroitsBons } from '../auth/useDroits';
import { Champ, ErreurFormulaire, Panneau } from '../composants/Formulaire';
import { DESCRIPTION_MODELE, LIBELLE_MODELE, MODELES_BON, MODELE_PAR_DEFAUT } from '../domaine/bon-modeles';
import { signalerModifications } from '../domaine/garde-modifications';
import {
  ajouterControle,
  deplacerControle,
  estModifiee,
  modificationEnvoyee,
  phraseControles,
  problemesNature,
  rangPourNouvelleNature,
  refusDeModele,
  renommerControle,
  retirerControle,
  versEditable,
  type NatureEditable,
} from '../domaine/reglages-bons';

export function ReglagesBonsPage() {
  const droits = useDroitsBons();
  const [parametres, setParametres] = useSearchParams();
  const natureId = parametres.get('nature');

  const [natures, setNatures] = useState<NatureBon[] | null>(null);
  const [erreurListe, setErreurListe] = useState<string | null>(null);
  const [archivees, setArchivees] = useState(false);
  const [succes, setSucces] = useState<string | null>(null);
  const [erreurAction, setErreurAction] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [creation, setCreation] = useState(false);

  const charger = useCallback(async () => {
    try {
      setNatures(await api.naturesBon(true));
      setErreurListe(null);
    } catch (e) {
      setNatures((n) => n ?? []);
      setErreurListe(e instanceof ApiError ? e.message : 'Les natures de bon n’ont pas pu être lues.');
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const visibles = useMemo(() => (natures ?? []).filter((n) => archivees || !n.archived), [natures, archivees]);
  const nature = (natures ?? []).find((n) => n.id === natureId) ?? null;

  // L'édition en cours de la nature choisie. Elle repart de la nature relue quand son identité ou sa révision change.
  const origine = useMemo(() => (nature ? versEditable(nature) : null), [nature]);
  const [courant, setCourant] = useState<NatureEditable | null>(null);
  const [problemes, setProblemes] = useState<string[]>([]);
  const [conflit, setConflit] = useState(false);
  useEffect(() => {
    setCourant(origine);
    setProblemes([]);
    setConflit(false);
  }, [origine]);

  const modifie = origine !== null && courant !== null && estModifiee(origine, courant);
  useEffect(() => {
    signalerModifications(modifie, 'Vous avez des modifications non enregistrées dans les réglages des bons.\n\nQuitter maintenant les fera perdre.');
    return () => signalerModifications(false);
  }, [modifie]);

  const choisir = (id: string) => {
    if (modifie && !window.confirm('Abandonner vos modifications non enregistrées ?')) return;
    const p = new URLSearchParams(parametres);
    p.set('nature', id);
    setParametres(p, { replace: true });
    setSucces(null);
    setErreurAction(null);
  };

  const modifier = (fn: (n: NatureEditable) => NatureEditable) => {
    setCourant((c) => (c ? fn(c) : c));
    setProblemes([]);
    setErreurAction(null);
    setSucces(null);
  };

  const enregistrer = async () => {
    if (!nature || !origine || !courant) return;
    const trouves = problemesNature(courant);
    if (trouves.length > 0) {
      setProblemes(trouves);
      return;
    }
    setEnCours(true);
    setErreurAction(null);
    setConflit(false);
    try {
      await api.modifierNatureBon(nature.id, modificationEnvoyee(origine, courant, nature.revision));
      // Relire AVANT d'annoncer : une erreur posée avant le rechargement serait effacée par lui.
      await charger();
      setSucces('Réglages enregistrés. Les bons déjà saisis gardent les libellés qu’ils ont reçus ; les prochains reprennent cette liste.');
    } catch (e) {
      // Un bon porte déjà la nature : le modèle de feuille ne change plus. C'est un 409 aussi, mais à CORRIGER, pas à recharger.
      if (e instanceof ApiError && e.status === 409 && !refusDeModele(e.message)) setConflit(true);
      else setErreurAction(e instanceof Error ? e : new Error('Enregistrement impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const archiver = async (archive: boolean) => {
    if (!nature) return;
    if (archive && !window.confirm(`Archiver la nature « ${nature.label} » ? Elle ne sera plus proposée pour un nouveau bon ; les bons existants gardent leur nature.`)) return;
    setEnCours(true);
    setErreurAction(null);
    try {
      await api.modifierNatureBon(nature.id, { revision: nature.revision, archived: archive });
      await charger();
      setSucces(archive ? 'Nature archivée.' : 'Nature réactivée.');
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setConflit(true);
      else setErreurAction(e instanceof Error ? e : new Error('Opération impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const semer = async () => {
    setEnCours(true);
    setErreurAction(null);
    try {
      const bilan = await api.semerNaturesBon();
      await charger();
      const morceaux = [
        bilan.creees.length > 0 ? `${bilan.creees.length} nature${bilan.creees.length > 1 ? 's' : ''} installée${bilan.creees.length > 1 ? 's' : ''}` : null,
        bilan.controlesInstalles.length > 0
          ? `contrôles de départ installés sur ${bilan.controlesInstalles.length} nature${bilan.controlesInstalles.length > 1 ? 's' : ''} qui n’en avaient pas`
          : null,
        bilan.modelesInstalles.length > 0
          ? `modèle de feuille posé sur ${bilan.modelesInstalles.length} nature${bilan.modelesInstalles.length > 1 ? 's' : ''} de départ restée${bilan.modelesInstalles.length > 1 ? 's' : ''} sur « Entretien » (aucun bon ne les portait)`
          : null,
      ].filter(Boolean);
      setSucces(morceaux.length > 0 ? `${morceaux.join(' ; ')}.` : 'Rien à installer : les natures de départ et leurs contrôles sont déjà là.');
    } catch (e) {
      setErreurAction(e instanceof Error ? e : new Error('Installation impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const peutEcrire = droits.peutConfigurerNatures;
  const messages = (
    <>
      {succes && <div className="message succes">{succes}</div>}
      <ErreurFormulaire
        message={erreurAction?.message ?? null}
        details={erreurAction instanceof ApiError ? erreurAction.details : undefined}
      />
    </>
  );

  return (
    <div className="gammes-page">
      <div className="entete-page">
        <div>
          <div className="fil-ariane">
            <Link to="/interventions">← Interventions</Link>
          </div>
          <h1>Réglages des bons</h1>
          <p>Les natures de bon et, pour chacune, la liste de ses contrôles : c’est elle qui s’imprime en trois colonnes sur le papier.</p>
        </div>
        {peutEcrire && (
          <div className="saisie-ligne">
            <button className="secondaire" disabled={enCours || modifie} onClick={() => void semer()} title="Installe les natures de départ et leurs contrôles quand ils manquent ; ne touche à rien de ce que vous avez réglé">
              Installer les natures de départ
            </button>
            <button disabled={enCours || modifie} onClick={() => setCreation(true)}>
              Nouvelle nature
            </button>
          </div>
        )}
      </div>

      {!peutEcrire && (
        <div className="message info">
          Vous consultez ces réglages en lecture seule : les modifier demande le droit de configurer les natures de bon
          (administrateur ou responsable).
        </div>
      )}

      <div className="gammes-mise-en-page">
        <div className="carte gammes-liste">
          <div className="gammes-liste-entete">
            <label className="saisie-ligne">
              <input type="checkbox" checked={archivees} onChange={(e) => setArchivees(e.target.checked)} /> Afficher les natures archivées
            </label>
          </div>
          <div className="gammes-liste-corps">
            {erreurListe && <div className="message erreur">{erreurListe}</div>}
            {natures === null ? (
              !erreurListe && <div className="vide">Chargement…</div>
            ) : visibles.length === 0 ? (
              <div className="vide">
                Aucune nature de bon.{peutEcrire ? ' Installez les natures de départ, ou créez la première.' : ''}
              </div>
            ) : (
              <ul className="parc-lignes">
                {visibles.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      className={`parc-ligne${n.id === natureId ? ' actif' : ''}`}
                      aria-pressed={n.id === natureId}
                      onClick={() => choisir(n.id)}
                    >
                      <div className="parc-ligne-titre">
                        <strong>{n.label}</strong>
                        {n.archived && <span className="etat revoque">Archivée</span>}
                      </div>
                      <div className="aide">
                        {LIBELLE_MODELE[n.layout]} · {phraseControles(n.checkItems.length)}
                        {n.recordsService ? ' · enregistre un entretien' : ''}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="gammes-fiche">
          {!nature && (
            <>
              {(erreurAction || succes) && <div className="gammes-fiche-entete">{messages}</div>}
              <div className="carte">
                <div className="vide">
                  Choisissez une nature dans la liste pour régler ses contrôles. Un contrôle se coche ensuite à la saisie du bon,
                  jamais d’office.
                </div>
              </div>
            </>
          )}

          {nature && courant && origine && (
            <>
              <div className="gammes-fiche-entete">
                <div className="gammes-fiche-titre">
                  <h2>
                    {nature.label}{' '}
                    {nature.archived && <span className="etat revoque">Archivée</span>}
                  </h2>
                  {peutEcrire && (
                    <div className="saisie-ligne">
                      <button
                        className="secondaire"
                        disabled={enCours || modifie}
                        title={modifie ? 'Enregistrez ou abandonnez vos modifications d’abord' : undefined}
                        onClick={() => void archiver(!nature.archived)}
                      >
                        {nature.archived ? 'Réactiver' : 'Archiver'}
                      </button>
                    </div>
                  )}
                </div>
                {messages}
                {conflit && (
                  <div className="message erreur">
                    Cette nature a été modifiée par quelqu’un d’autre depuis votre ouverture : rechargez-la avant d’enregistrer, sinon
                    vous écraseriez son travail.{' '}
                    <button type="button" className="secondaire" onClick={() => void charger()}>
                      Recharger la nature (vos modifications non enregistrées seront perdues)
                    </button>
                  </div>
                )}
              </div>

              <section className="carte">
                <header className="entete-carte">
                  <h3>La nature</h3>
                </header>
                <div className="carte-corps">
                  <Champ libelle="Libellé" requis>
                    <input
                      type="text"
                      maxLength={120}
                      value={courant.label}
                      disabled={!peutEcrire || enCours}
                      onChange={(e) => modifier((n) => ({ ...n, label: e.target.value }))}
                    />
                  </Champ>
                  <Champ libelle="Modèle de feuille">
                    <select
                      value={courant.layout}
                      disabled={!peutEcrire || enCours}
                      onChange={(e) => modifier((n) => ({ ...n, layout: e.target.value as ModeleBon }))}
                    >
                      {MODELES_BON.map((m) => (
                        <option key={m} value={m}>
                          {LIBELLE_MODELE[m]}
                        </option>
                      ))}
                    </select>
                    <p className="aide">
                      {DESCRIPTION_MODELE[courant.layout]} Le modèle ne change plus dès qu’un bon porte la nature : créez-en une autre pour
                      un autre modèle.
                    </p>
                  </Champ>
                  <label className="saisie-ligne">
                    <input
                      type="checkbox"
                      checked={courant.recordsService}
                      disabled={!peutEcrire || enCours}
                      onChange={(e) => modifier((n) => ({ ...n, recordsService: e.target.checked }))}
                    />
                    Ce type de bon enregistre un entretien réalisé (palier de la gamme)
                  </label>
                  <p className="aide">
                    Le code de la nature (« {nature.code} ») ne change jamais : c’est son identité. Couper cette option rend non
                    soumettables les bons qui portent déjà un palier, jusqu’à ce qu’on le retire.
                  </p>
                </div>
              </section>

              <section className="carte">
                <header className="entete-carte">
                  <h3>Contrôles et inspections</h3>
                  <span className="aide">{phraseControles(courant.controles.length)}</span>
                </header>
                <div className="carte-corps">
                  <p className="aide">
                    Dans l’ordre où ils s’impriment, trois colonnes remplies colonne par colonne. Renommer ou réordonner ne change
                    jamais un bon déjà saisi.
                  </p>
                  {courant.controles.length === 0 && <div className="vide">Aucun contrôle : cette nature n’imprime pas la rubrique.</div>}
                  {courant.controles.map((c, i) => (
                    <div className="ligne-controle" key={c.key}>
                      <input
                        type="text"
                        aria-label={`Libellé du contrôle ${i + 1}`}
                        maxLength={120}
                        value={c.label}
                        disabled={!peutEcrire || enCours}
                        placeholder="Libellé du contrôle"
                        onChange={(e) => modifier((n) => renommerControle(n, i, e.target.value))}
                      />
                      {peutEcrire && (
                        <>
                          <button type="button" className="secondaire discret" title="Monter" disabled={enCours || i === 0} onClick={() => modifier((n) => deplacerControle(n, i, -1))}>
                            ↑
                          </button>
                          <button
                            type="button"
                            className="secondaire discret"
                            title="Descendre"
                            disabled={enCours || i === courant.controles.length - 1}
                            onClick={() => modifier((n) => deplacerControle(n, i, 1))}
                          >
                            ↓
                          </button>
                          <button type="button" className="secondaire discret" title="Retirer ce contrôle" disabled={enCours} onClick={() => modifier((n) => retirerControle(n, i))}>
                            ✕
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  {peutEcrire && (
                    <div className="saisie-ligne">
                      <button type="button" className="secondaire" disabled={enCours} onClick={() => modifier((n) => ajouterControle(n))}>
                        + Ajouter un contrôle
                      </button>
                    </div>
                  )}
                </div>
              </section>

              {peutEcrire && (
                <div className="editeur-barre">
                  {problemes.length > 0 && (
                    <div className="message erreur">
                      Corrigez {problemes.length} point{problemes.length > 1 ? 's' : ''} avant d’enregistrer :
                      <ul className="details-erreur">
                        {problemes.slice(0, 10).map((p, i) => (
                          <li key={`${p}-${i}`}>{p}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="editeur-barre-actions">
                    <span className={modifie ? 'etat attente' : 'aide'}>{modifie ? 'Modifications non enregistrées' : 'Aucune modification'}</span>
                    <div className="saisie-ligne">
                      <button type="button" className="secondaire" disabled={enCours || !modifie} onClick={() => setCourant(origine)}>
                        Abandonner les modifications
                      </button>
                      <button type="button" disabled={enCours || !modifie} onClick={() => void enregistrer()}>
                        {enCours ? 'Enregistrement…' : 'Enregistrer'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {creation && natures && (
        <NouvelleNature
          rang={rangPourNouvelleNature(natures)}
          onFermer={() => setCreation(false)}
          onCreee={async (creee) => {
            setCreation(false);
            await charger();
            setSucces(`Nature « ${creee.label} » créée : ajoutez-lui maintenant ses contrôles.`);
            const p = new URLSearchParams(parametres);
            p.set('nature', creee.id);
            setParametres(p, { replace: true });
          }}
        />
      )}
    </div>
  );
}

/** « Nouvelle nature » : un libellé et l'option ; le code se déduit du libellé, les contrôles s'ajoutent ensuite. */
function NouvelleNature({
  rang,
  onFermer,
  onCreee,
}: {
  rang: number;
  onFermer: () => void;
  onCreee: (nature: NatureBon) => void | Promise<void>;
}) {
  const [libelle, setLibelle] = useState('');
  const [layout, setLayout] = useState<ModeleBon>(MODELE_PAR_DEFAUT);
  const [entretien, setEntretien] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);

  const creer = async () => {
    if (libelle.trim() === '') {
      setErreur(new Error('Le libellé de la nature est obligatoire.'));
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      const creee = await api.creerNatureBon({ label: libelle.trim(), layout, recordsService: entretien, sortOrder: rang });
      await onCreee(creee);
    } catch (e) {
      setErreur(e instanceof Error ? e : new Error('Création impossible.'));
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre="Nouvelle nature de bon"
      sousTitre="Le code se déduit du libellé et ne change plus ensuite."
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={() => void creer()} disabled={enCours}>
            {enCours ? 'Création…' : 'Créer la nature'}
          </button>
        </>
      }
    >
      <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
      <Champ libelle="Libellé" requis>
        <input type="text" autoFocus maxLength={120} value={libelle} onChange={(e) => setLibelle(e.target.value)} />
      </Champ>
      <Champ libelle="Modèle de feuille" requis>
        <select value={layout} onChange={(e) => setLayout(e.target.value as ModeleBon)}>
          {MODELES_BON.map((m) => (
            <option key={m} value={m}>
              {LIBELLE_MODELE[m]}
            </option>
          ))}
        </select>
        <p className="aide">{DESCRIPTION_MODELE[layout]} Il ne change plus dès qu’un bon porte la nature.</p>
      </Champ>
      <label className="saisie-ligne">
        <input type="checkbox" checked={entretien} onChange={(e) => setEntretien(e.target.checked)} />
        Ce type de bon enregistre un entretien réalisé
      </label>
    </Panneau>
  );
}
